import { Link } from 'react-router-dom';
import logoSrc from '@/assets/logos/radium-logo-text.svg';
import { ElementCard } from '@/components/content/ElementCard';

interface FooterColProps {
  title: string;
  links: { href: string; label: string; external?: boolean }[];
}

function FooterCol({ title, links }: FooterColProps) {
  return (
    <div>
      <div
        className="text-eyebrow"
        style={{ marginBottom: 16, color: 'var(--ink-muted)' }}
      >
        {title}
      </div>
      <ul style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {links.map((l) => (
          <li key={l.href + l.label}>
            {l.external ? (
              <a
                href={l.href}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 14,
                  color: 'var(--ink-secondary)',
                  transition: 'color 160ms var(--ease-out)',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = 'var(--ink-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = 'var(--ink-secondary)')
                }
              >
                {l.label}
              </a>
            ) : (
              <Link
                to={l.href}
                style={{
                  fontSize: 14,
                  color: 'var(--ink-secondary)',
                  transition: 'color 160ms var(--ease-out)',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = 'var(--ink-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = 'var(--ink-secondary)')
                }
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer
      style={{
        position: 'relative',
        borderTop: '1px solid var(--border-subtle)',
        marginTop: 96,
        padding: '64px 0 32px',
      }}
    >
      <div className="container">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
            gap: 40,
            marginBottom: 64,
          }}
          className="footer-grid"
        >
          <div>
            <Link to="/" aria-label="Radium home">
              <img
                src={logoSrc}
                alt="Radium"
                style={{
                  height: 28,
                  width: 'auto',
                  filter: 'drop-shadow(0 0 8px var(--radium-glow))',
                }}
              />
            </Link>
            <p
              style={{
                marginTop: 16,
                color: 'var(--ink-muted)',
                maxWidth: 320,
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              The AI-first personalization framework for the surfaces you own.
            </p>
            <div style={{ marginTop: 24 }}>
              <ElementCard symbol="Ra" number="88" label="Radium" size="sm" />
            </div>
          </div>
          <FooterCol
            title="Site"
            links={[
              { href: '/', label: 'Home' },
              { href: '/personalization', label: 'Personalization' },
              { href: '/personalization/customers', label: 'Customers' },
              { href: '/about', label: 'About' },
              { href: '/contact', label: 'Contact' },
            ]}
          />
          <FooterCol
            title="Open source"
            links={[
              { href: '/orchestration', label: 'Orchestration' },
              { href: '/features', label: 'Features' },
              { href: '/use-cases', label: 'Use cases' },
              { href: '/examples', label: 'Examples' },
              { href: '/community', label: 'Community' },
              { href: '/docs/introduction', label: 'Documentation' },
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              { href: '/legal/privacy', label: 'Privacy' },
              { href: '/legal/terms', label: 'Terms' },
            ]}
          />
          <FooterCol
            title="Studio"
            links={[
              {
                href: 'https://unicorn.love',
                label: 'Unicorn ↗',
                external: true,
              },
              {
                href: 'mailto:hello@radium.love',
                label: 'hello@radium.love',
                external: true,
              },
            ]}
          />
        </div>

        <div
          style={{
            paddingTop: 24,
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ color: 'var(--ink-muted)', fontSize: 13 }}>
            <span className="text-mono">
              © Unicorn Love LLC · {new Date().getFullYear()}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            <span>Made by</span>
            <a
              href="https://unicorn.love"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--ink-secondary)',
                fontWeight: 500,
                borderBottom: '1px solid var(--border-default)',
                paddingBottom: 1,
                textDecoration: 'none',
              }}
            >
              Unicorn ↗
            </a>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .footer-grid { grid-template-columns: 1fr 1fr 1fr !important; }
        }
        @media (max-width: 768px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
          footer { margin-top: 64px !important; }
        }
        @media (max-width: 480px) {
          .footer-grid { grid-template-columns: 1fr !important; }
          footer { margin-top: 48px !important; }
        }
      `}</style>
    </footer>
  );
}
